// Network status hook with three states:
//   'online'  – fresh ping replied within SLOW_THRESHOLD_MS
//   'slow'    – fresh ping replied but slowly (rural Germany etc.)
//   'offline' – browser reports offline OR ping failed/timed out
//
// Strategy:
//   - Initial value from navigator.onLine
//   - Listens for window 'online'/'offline' events for instant updates
//   - Polls a tiny HEAD request to a non-cacheable Supabase endpoint
//     every PING_INTERVAL_MS to detect "online but lahm" / dead links

import { useEffect, useState } from 'react'

export type OnlineStatus = 'online' | 'slow' | 'offline'

const PING_INTERVAL_MS = 60_000
const PING_TIMEOUT_MS = 5_000
const SLOW_THRESHOLD_MS = 1_500
const PING_INITIAL_DELAY_MS = 10_000

function getInitialStatus(): OnlineStatus {
  if (typeof navigator === 'undefined') return 'online'
  return navigator.onLine ? 'online' : 'offline'
}

function buildPingUrl(): string | null {
  const custom = import.meta.env.VITE_PING_URL as string | undefined
  if (custom) {
    const sep = custom.includes('?') ? '&' : '?'
    return `${custom}${sep}_=${Date.now()}`
  }

  // Fallback: hit the Supabase REST root with the anon key. Clean
  // 200 response, no 401 console spam.
  const url = import.meta.env.VITE_SUPABASE_URL
  const apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !apiKey) return null
  return `${url}/rest/v1/?apikey=${apiKey}&_=${Date.now()}`
}

async function pingOnce(): Promise<OnlineStatus> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'offline'
  }

  const target = buildPingUrl()
  if (!target) return 'online'

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS)

  const start = Date.now()

  try {
    const res = await fetch(target, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
      mode: 'cors',
    })
    const latency = Date.now() - start
    if (res.status >= 500) return 'offline'
    return latency > SLOW_THRESHOLD_MS ? 'slow' : 'online'
  } catch {
    return 'offline'
  } finally {
    clearTimeout(timeoutId)
  }
}

export function useOnlineStatus(): OnlineStatus {
  const [status, setStatus] = useState<OnlineStatus>(getInitialStatus)

  useEffect(() => {
    let cancelled = false

    const handleOnline = () => {
      if (cancelled) return
      setStatus('online')
      void runPing()
    }

    const handleOffline = () => {
      if (cancelled) return
      setStatus('offline')
    }

    async function runPing() {
      // Skip ping when tab is hidden — avoid pointless network activity
      // and the browser's loading spinner showing in the address bar.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      const next = await pingOnce()
      if (cancelled) return
      setStatus(next)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Delay first ping so the initial page load isn't slowed down.
    const firstPingTimer = setTimeout(runPing, PING_INITIAL_DELAY_MS)
    const interval = setInterval(runPing, PING_INTERVAL_MS)

    return () => {
      cancelled = true
      clearTimeout(firstPingTimer)
      clearInterval(interval)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return status
}
